// Agent provisioning seed — the JSON a publisher server's agent-runtime
// imports (`agent-runtime provision --keys <file>`).
//
// TEMPORARY mechanism, by decision 2026-08-10: the seed file exists only
// because the runtime cannot yet fetch the agent's keys from the principal's
// keybag (agent-runtime ProvisionCommand marks keybag-fetch as future work).
// The create-agent dialog offers this download ONCE, while the freshly
// generated private keys are still in memory — afterwards they exist only
// encrypted in the principal's keybag, which the UI never exports. When
// keybag-fetch provisioning lands (option A), this builder and the download
// step should be removed.
//
// Shape contract: apps/publisher/src/agent-runtime/AgentKeyMaterial.cs —
// `agentCid` + `keys.efs_encrypt.{publicKey,privateKey,fingerprintHex}` +
// `keys.agent_signing_key.{publicKey,privateSeed,fingerprintHex}` required;
// fingerprints are lowercase SHA-512 hex over the raw public-key bytes;
// principalCid/orgCid/createdAt optional but carried into the store;
// attr/msg keypairs unused today but kept so later milestones need no
// re-provisioning. (Same shape apps/cid/dev/create-agent.ts writes, minus
// the principal-credentials block the store projection drops anyway.)

import * as aes from './aes';

interface KemKeypair {
    publicKey: Uint8Array;
    secretKey: Uint8Array;
}

interface SigningKeypair {
    publicKey: Uint8Array;
    secretSeed: Uint8Array;
}

export interface AgentProvisioningSeed {
    agentCid: string;
    principalCid?: string;
    orgCid?: string;
    createdAt: string;
    keys: {
        attr_encrypt: { algorithm: string; publicKey: string; privateKey: string };
        msg_encrypt: { algorithm: string; publicKey: string; privateKey: string };
        efs_encrypt: { algorithm: string; publicKey: string; privateKey: string; fingerprintHex: string };
        agent_signing_key: { algorithm: string; publicKey: string; privateSeed: string; fingerprintHex: string };
    };
}

export async function buildAgentProvisioningSeed(args: {
    agentCid: string;
    principalCid?: string;
    orgCid?: string;
    attr: KemKeypair;
    msg: KemKeypair;
    efs: KemKeypair;
    signing: SigningKeypair;
}): Promise<AgentProvisioningSeed> {
    const seed: AgentProvisioningSeed = {
        agentCid: args.agentCid,
        createdAt: new Date().toISOString(),
        keys: {
            attr_encrypt: {
                algorithm: 'X25519-MLKem1024',
                publicKey: aes.toBase64(args.attr.publicKey),
                privateKey: aes.toBase64(args.attr.secretKey),
            },
            msg_encrypt: {
                algorithm: 'X25519-MLKem1024',
                publicKey: aes.toBase64(args.msg.publicKey),
                privateKey: aes.toBase64(args.msg.secretKey),
            },
            efs_encrypt: {
                algorithm: 'X25519-MLKem1024',
                publicKey: aes.toBase64(args.efs.publicKey),
                privateKey: aes.toBase64(args.efs.secretKey),
                fingerprintHex: aes.toHex(await aes.sha512(args.efs.publicKey)),
            },
            agent_signing_key: {
                algorithm: 'Ed25519-MLDsa65',
                publicKey: aes.toBase64(args.signing.publicKey),
                privateSeed: aes.toBase64(args.signing.secretSeed),
                fingerprintHex: aes.toHex(await aes.sha512(args.signing.publicKey)),
            },
        },
    };
    if (args.principalCid !== undefined) seed.principalCid = args.principalCid;
    if (args.orgCid !== undefined) seed.orgCid = args.orgCid;
    return seed;
}
